/**
 * Order Status Mapping — Presentation Layer Only
 *
 * Maps raw ERPNext Sales Order status strings to merchant-friendly labels.
 * Supports EN + TH. Derives "Partially Shipped" from per_delivered field.
 *
 * For marketplace orders (Shopee, Lazada, TikTok): the marketplace platform
 * owns the order lifecycle, so we display the marketplace status instead of
 * ERPNext's fulfillment status. ERPNext status stays "To Deliver and Bill"
 * because no DN/SI is created — but the merchant needs to see the real
 * marketplace status (COMPLETED, SHIPPED, etc.). This is display-only;
 * the underlying ERPNext document is unchanged.
 *
 * IMPORTANT: This is display-only. All business logic (derivePaymentStatus,
 * canShip checks, StorefrontOrderBanner) must continue using raw ERPNext values.
 */

import type { StatusColor } from "@/components/ui/status-badge";

interface StatusEntry {
  en: string;
  th: string;
  color: StatusColor;
}

const ORDER_STATUS_MAP: Record<string, StatusEntry> = {
  Draft: { en: "Draft", th: "แบบร่าง", color: "gray" },
  "To Deliver and Bill": { en: "Processing", th: "กำลังดำเนินการ", color: "orange" },
  "To Deliver": { en: "Ready to Ship", th: "พร้อมจัดส่ง", color: "blue" },
  "To Bill": { en: "Shipped (Unpaid)", th: "จัดส่งแล้ว (ยังไม่ชำระ)", color: "purple" },
  Completed: { en: "Delivered", th: "จัดส่งสำเร็จ", color: "green" },
  Cancelled: { en: "Cancelled", th: "ยกเลิก", color: "red" },
  "On Hold": { en: "On Hold", th: "พักไว้", color: "yellow" },
  Overdue: { en: "Overdue", th: "เลยกำหนด", color: "red" },
  Closed: { en: "Closed", th: "ปิดแล้ว", color: "gray" },
  // Refund states (Plan 1929) — custom_refund_status on Sales Order.
  Refunded: { en: "Refunded", th: "คืนเงินแล้ว", color: "red" },
  "Partially Refunded": { en: "Partially Refunded", th: "คืนเงินบางส่วน", color: "orange" },
};

/**
 * Marketplace status map — maps raw marketplace platform statuses to
 * merchant-friendly labels. These statuses come from the marketplace API
 * (Shopee, Lazada, TikTok) and reflect the actual order lifecycle on
 * the platform, not ERPNext's fulfillment pipeline.
 */
const MARKETPLACE_STATUS_MAP: Record<string, StatusEntry> = {
  // Shopee statuses
  UNPAID:             { en: "Unpaid",              th: "ยังไม่ชำระ",         color: "yellow" },
  READY_TO_SHIP:      { en: "Ready to Ship",       th: "พร้อมจัดส่ง",       color: "blue" },
  PROCESSED:          { en: "Processed",           th: "ดำเนินการแล้ว",     color: "blue" },
  SHIPPED:            { en: "Shipped",             th: "จัดส่งแล้ว",        color: "purple" },
  TO_CONFIRM_RECEIVE: { en: "Awaiting Delivery",   th: "รอรับสินค้า",       color: "purple" },
  COMPLETED:          { en: "Completed",           th: "สำเร็จ",            color: "green" },
  CANCELLED:          { en: "Cancelled",           th: "ยกเลิก",           color: "red" },
  IN_CANCEL:          { en: "Cancelling",          th: "กำลังยกเลิก",      color: "orange" },
  TO_RETURN:          { en: "Return Requested",    th: "ขอคืนสินค้า",      color: "orange" },
  // Lazada statuses
  pending:            { en: "Pending",             th: "รอดำเนินการ",       color: "yellow" },
  packed:             { en: "Packed",              th: "แพ็คแล้ว",          color: "blue" },
  ready_to_ship:      { en: "Ready to Ship",       th: "พร้อมจัดส่ง",       color: "blue" },
  shipped:            { en: "Shipped",             th: "จัดส่งแล้ว",        color: "purple" },
  delivered:          { en: "Delivered",            th: "จัดส่งสำเร็จ",      color: "green" },
  canceled:           { en: "Cancelled",           th: "ยกเลิก",           color: "red" },
  returned:           { en: "Returned",            th: "คืนสินค้า",         color: "red" },
  failed:             { en: "Failed",              th: "ล้มเหลว",          color: "red" },
};

const PARTIALLY_SHIPPED: StatusEntry = {
  en: "Partially Shipped",
  th: "จัดส่งบางส่วน",
  color: "blue",
};

/** Active delivery states where partial-ship derivation applies */
const ACTIVE_DELIVERY_STATES = new Set(["To Deliver and Bill", "To Deliver"]);

export interface OrderStatusDisplay {
  label: string;
  color: StatusColor;
  rawStatus: string;
}

/**
 * Resolve a raw ERPNext status to a merchant-friendly display object.
 *
 * For marketplace orders: when marketplaceStatus is provided, it takes
 * precedence over ERPNext's fulfillment status. This is because marketplace
 * orders are completed externally — ERPNext's status stays "To Deliver and
 * Bill" (no DN/SI created), but the merchant needs to see the actual
 * marketplace lifecycle (COMPLETED, SHIPPED, etc.).
 *
 * @param rawStatus  - The raw `status` field from the Sales Order
 * @param perDelivered - The `per_delivered` float (0-100) for partial-ship derivation
 * @param language   - "en" or "th"
 * @param marketplaceStatus - Optional marketplace platform status (e.g. shopee_order_status)
 * @param paymentVerified - When true and status is "To Deliver and Bill", show "Paid" instead of "Processing"
 */
export function getOrderStatusDisplay(
  rawStatus: string,
  perDelivered: number = 0,
  language: "en" | "th" = "en",
  marketplaceStatus?: string,
  paymentVerified?: boolean,
  refundStatus?: string,
): OrderStatusDisplay {
  // Fully refunded takes precedence over everything (money returned, order closed out).
  // Partially refunded does NOT override — the order is still active; the detail
  // banner surfaces the partial-refund badge separately.
  if (refundStatus === "Refunded") {
    const entry = ORDER_STATUS_MAP.Refunded;
    return { label: entry[language], color: entry.color, rawStatus };
  }

  // Marketplace orders: prefer the marketplace platform status
  if (marketplaceStatus) {
    const mktEntry = MARKETPLACE_STATUS_MAP[marketplaceStatus];
    if (mktEntry) {
      return { label: mktEntry[language], color: mktEntry.color, rawStatus };
    }
    // Unknown marketplace status — show it directly with a neutral color
    return { label: marketplaceStatus, color: "gray", rawStatus };
  }

  // Payment-verified "To Deliver and Bill" → show as paid, awaiting shipment
  if (rawStatus === "To Deliver and Bill" && paymentVerified) {
    const label = language === "th" ? "ชำระเงินแล้ว · รอส่งของ" : "Paid · Awaiting Shipment";
    return { label, color: "green", rawStatus };
  }

  // Derive "Partially Shipped" only for active delivery states
  if (
    perDelivered > 0 &&
    perDelivered < 100 &&
    ACTIVE_DELIVERY_STATES.has(rawStatus)
  ) {
    return {
      label: PARTIALLY_SHIPPED[language],
      color: PARTIALLY_SHIPPED.color,
      rawStatus,
    };
  }

  const entry = ORDER_STATUS_MAP[rawStatus];
  if (entry) {
    return { label: entry[language], color: entry.color, rawStatus };
  }

  // Fallback: show raw status as-is with gray badge
  return { label: rawStatus || "Unknown", color: "gray", rawStatus };
}

/**
 * Get the friendly label for a status pill (no partial-ship derivation).
 * Used by SalesOrderStatusFlow where each pill represents a fixed workflow step.
 */
export function getStatusPillLabel(
  rawStatus: string,
  language: "en" | "th" = "en",
): string {
  const entry = ORDER_STATUS_MAP[rawStatus];
  return entry ? entry[language] : rawStatus;
}
